/**
 * Parser for DJI flight log files.
 *
 * Supports:
 * - DJI Fly / DJI GO 4 CSV exports (flight records)
 * - PhantomHelp / Airdata style CSV dumps
 * - Plain-text logs with OSD lines (fallback heuristic parser)
 *
 * Output format (FlightData):
 * {
 *   drone_model, drone_serial,
 *   start_time (ISO), end_time (ISO),
 *   duration_seconds, distance_meters,
 *   max_altitude_meters, max_speed_ms,
 *   battery_start_pct, battery_end_pct,
 *   home_lat, home_lng,
 *   gps_track: [{ lat, lng, alt, ts }],
 *   detected_format
 * }
 */

// --- Utilities ---
function toBase64(u8) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return btoa(bin);
}

// Map raw RTK status code to human label (DJI convention)
function classifyRtkStatus(code) {
  if (code === null || code === undefined) return null;
  const s = parseInt(code, 10);
  if (s === 50) return 'Fix';
  if (s === 16) return 'Float';
  if (s === 0) return 'Single';
  if (s === 1 || s === 2) return 'DGPS';
  return `Status ${s}`;
}

const HEADER_SYNONYMS = {
  ts: ['time(millisecond)', 'time(ms)', 'time(utc)', 'gps:datetime', 'datetime(utc)', 'time', 'timestamp'],
  lat: ['osd.latitude', 'osd:latitude', 'latitude', 'gps_lat', 'gps.lat', 'gpslat'],
  lng: ['osd.longitude', 'osd:longitude', 'longitude', 'gps_lng', 'gps.lon', 'gpslon', 'gps_lon'],
  alt: ['osd.altitude [ft]', 'osd.altitude [m]', 'osd:altitude', 'altitude(ft)', 'altitude(m)', 'altitude', 'height(m)', 'height', 'gps_alt'],
  speed_h: ['osd.hspeed [mph]', 'osd.hspeed [m/s]', 'osd.hspeed', 'speed', 'h_speed(m/s)'],
  speed_v: ['osd.vpsspeed [m/s]', 'osd.vpsspeed', 'vpsspeed'],
  battery: ['battery:chargelevel', 'battery(%)', 'battery %', 'battery', 'smartbattery:chargelevel'],
  drone: ['aircraft_name', 'aircraftname', 'ac_name', 'drone'],
  serial: ['aircraft_sn', 'aircraftsn', 'sn', 'serial'],
  home_lat: ['home_latitude', 'home.lat', 'homelat'],
  home_lng: ['home_longitude', 'home.lng', 'home.lon', 'homelng'],
  rtk_status: ['rtkdata.status_flag', 'rtkdata.pos_flagstat', 'rtk_status', 'rtk.status', 'rtkstatusflag', 'rtkdata:status_flag'],
  rtk_h_acc: ['rtkdata.hacc', 'rtkdata:hacc', 'rtk_hacc', 'h_accuracy', 'horizontal_accuracy'],
  rtk_v_acc: ['rtkdata.vacc', 'rtkdata:vacc', 'rtk_vacc', 'v_accuracy', 'vertical_accuracy'],
  satellites: ['osd.gpsnum', 'gpsnum', 'satellites', 'sats_used', 'numofsats'],
};

function normHeader(h) {
  return (h || '').toString().trim().toLowerCase().replace(/^"/, '').replace(/"$/, '');
}

function buildColIndex(headers) {
  const idx = {};
  const nh = headers.map(normHeader);
  for (const key of Object.keys(HEADER_SYNONYMS)) {
    for (const syn of HEADER_SYNONYMS[key]) {
      const i = nh.indexOf(syn);
      if (i >= 0) { idx[key] = i; break; }
    }
  }
  // Broader fallback - partial match
  for (const key of Object.keys(HEADER_SYNONYMS)) {
    if (idx[key] !== undefined) continue;
    for (let i = 0; i < nh.length; i++) {
      for (const syn of HEADER_SYNONYMS[key]) {
        if (nh[i].includes(syn)) { idx[key] = i; break; }
      }
      if (idx[key] !== undefined) break;
    }
  }
  return idx;
}

function toNumber(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function parseTimestamp(v, anchorMs) {
  if (!v && v !== 0) return null;
  const s = String(v).trim();
  // Epoch millis?
  if (/^\d{13}$/.test(s)) return new Date(parseInt(s, 10)).toISOString();
  if (/^\d{10}$/.test(s)) return new Date(parseInt(s, 10) * 1000).toISOString();
  // Epoch seconds with decimals?
  const asNum = parseFloat(s);
  if (Number.isFinite(asNum) && asNum > 1_000_000_000 && asNum < 3_000_000_000) {
    return new Date(asNum * 1000).toISOString();
  }
  if (Number.isFinite(asNum) && asNum > 1_000_000_000_000 && asNum < 3_000_000_000_000) {
    return new Date(asNum).toISOString();
  }
  // Relative offset from flight start (ms) — DJI CSV uses time(millisecond) starting at 0.
  // Accept any numeric value below 30 days (86_400_000 * 30) as a relative ms offset.
  if (Number.isFinite(asNum) && asNum >= 0 && asNum < 2_592_000_000) {
    const base = Number.isFinite(anchorMs) ? anchorMs : Date.now();
    return new Date(base + asNum).toISOString();
  }
  // ISO / common datetime
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString();
  return null;
}

// --- CSV parsing (simple - handles quoted commas) ---
function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (ch === ',' && !inQuotes) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return null;
  const headers = splitCsvLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    rows.push(splitCsvLine(lines[i]));
  }
  return { headers, rows };
}

// --- Geo helpers (haversine) ---
const R_EARTH = 6371000;
function haversine(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(a));
}

// --- Main parser ---
export async function parseDjiLog(file) {
  const name = file.name || '';
  const isDat = /\.dat$/i.test(name);
  const isCsv = /\.csv$/i.test(name);
  // DJI Fly/Pilot save their native flight records as binary-encrypted
  // "DJIFlightRecord_YYYY-MM-DD_[HH-MM-SS].txt" - treat as binary passthrough.
  const isDjiBinaryTxt = /^DJIFlightRecord.*\.txt$/i.test(name);
  const isTxt = /\.txt$/i.test(name) && !isDjiBinaryTxt;

  // .dat or DJIFlightRecord_*.txt binary passthrough: base64 + upload raw,
  // server keeps the file for external processing (PhantomHelp, DatCon, etc.)
  if (isDat || isDjiBinaryTxt) {
    const buf = new Uint8Array(await file.arrayBuffer());
    if (buf.length > 10 * 1024 * 1024) {
      throw new Error('Arquivo binario acima de 10MB. Contate suporte para logs maiores.');
    }
    const rawB64 = toBase64(buf);
    const now = new Date();
    // Try to extract the flight timestamp from the DJI filename if possible
    let startTime = now.toISOString();
    const m = name.match(/(\d{4})-(\d{2})-(\d{2})[_-]\[(\d{1,2})-(\d{2})-(\d{2})\]/);
    if (m) {
      try {
        startTime = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4].padStart(2, '0')}:${m[5]}:${m[6]}Z`).toISOString();
      } catch { /* keep now */ }
    }
    return {
      drone_model: isDjiBinaryTxt ? 'DJI (FlightRecord)' : 'DJI (.dat)',
      drone_serial: '',
      start_time: startTime,
      end_time: startTime,
      duration_seconds: 0,
      distance_meters: 0,
      max_altitude_meters: 0,
      max_speed_ms: 0,
      battery_start_pct: null,
      battery_end_pct: null,
      home_lat: null,
      home_lng: null,
      gps_track: [],
      file_name: name,
      detected_format: isDjiBinaryTxt ? 'DJIFlightRecord (binario)' : 'DAT (binario)',
      raw_log_base64: rawB64,
      notes: `Log binario enviado como raw (${(buf.length / 1024).toFixed(0)} KB). Administrador pode baixar e processar externamente.`,
    };
  }

  if (!isCsv && !isTxt) {
    throw new Error('Formato nao suportado. Use .csv, .txt ou .dat do DJI Fly/Pilot/GO 4.');
  }
  const text = await file.text();
  const parsed = parseCsv(text);
  if (!parsed) throw new Error('Arquivo vazio ou invalido');

  const { headers, rows } = parsed;
  const col = buildColIndex(headers);

  if (col.lat === undefined || col.lng === undefined) {
    throw new Error('CSV sem colunas de latitude/longitude reconhecidas');
  }

  const gps = [];
  let startTs = null;
  let endTs = null;
  let maxAlt = 0;
  let maxSpeed = 0;
  let firstBat = null;
  let lastBat = null;
  let firstLat = null;
  let firstLng = null;
  let distMeters = 0;
  let prev = null;
  let drone = '';
  let serial = '';
  let homeLat = null;
  let homeLng = null;
  // RTK aggregation
  const rtkCounts = { Fix: 0, Float: 0, Single: 0, DGPS: 0, Other: 0 };
  let rtkSamples = 0;
  let hAccSum = 0; let hAccN = 0;
  let vAccSum = 0; let vAccN = 0;
  let satsMax = 0;

  // Anchor: use file lastModified (if available) or current time as base for
  // relative `time(millisecond)` offsets. We detect if timestamps are relative
  // by checking the first row's time value.
  let anchorMs = Date.now();
  try {
    if (file.lastModified) anchorMs = file.lastModified;
  } catch { /* ignore */ }

  for (const row of rows) {
    const lat = toNumber(row[col.lat]);
    const lng = toNumber(row[col.lng]);
    if (lat === null || lng === null || (lat === 0 && lng === 0)) continue;

    let alt = col.alt !== undefined ? toNumber(row[col.alt]) : null;
    // If header explicitly says ft, convert
    const altHeader = normHeader(headers[col.alt] || '');
    if (alt !== null && altHeader.includes('ft')) alt = alt * 0.3048;

    let speedH = col.speed_h !== undefined ? toNumber(row[col.speed_h]) : null;
    const spdHeader = normHeader(headers[col.speed_h] || '');
    if (speedH !== null && spdHeader.includes('mph')) speedH = speedH * 0.44704;

    const bat = col.battery !== undefined ? toNumber(row[col.battery]) : null;
    const ts = col.ts !== undefined ? parseTimestamp(row[col.ts], anchorMs) : null;

    // RTK extraction
    if (col.rtk_status !== undefined) {
      const status = classifyRtkStatus(toNumber(row[col.rtk_status]));
      if (status) {
        rtkSamples++;
        if (rtkCounts[status] !== undefined) rtkCounts[status]++;
        else rtkCounts.Other++;
      }
    }
    if (col.rtk_h_acc !== undefined) {
      const v = toNumber(row[col.rtk_h_acc]);
      if (v !== null && v >= 0) { hAccSum += v; hAccN++; }
    }
    if (col.rtk_v_acc !== undefined) {
      const v = toNumber(row[col.rtk_v_acc]);
      if (v !== null && v >= 0) { vAccSum += v; vAccN++; }
    }
    if (col.satellites !== undefined) {
      const v = toNumber(row[col.satellites]);
      if (v !== null && v > satsMax) satsMax = v;
    }

    if (!drone && col.drone !== undefined) drone = (row[col.drone] || '').trim();
    if (!serial && col.serial !== undefined) serial = (row[col.serial] || '').trim();
    if (homeLat === null && col.home_lat !== undefined) homeLat = toNumber(row[col.home_lat]);
    if (homeLng === null && col.home_lng !== undefined) homeLng = toNumber(row[col.home_lng]);

    const point = { lat, lng, alt: alt || 0, ts: ts ? new Date(ts).getTime() : null };
    gps.push(point);

    if (firstLat === null) { firstLat = lat; firstLng = lng; }
    if (ts) { if (!startTs) startTs = ts; endTs = ts; }
    if (alt !== null && alt > maxAlt) maxAlt = alt;
    if (speedH !== null && speedH > maxSpeed) maxSpeed = speedH;
    if (bat !== null) {
      if (firstBat === null) firstBat = bat;
      lastBat = bat;
    }
    if (prev) distMeters += haversine(prev.lat, prev.lng, lat, lng);
    prev = point;
  }

  if (gps.length === 0) {
    throw new Error('Nenhum ponto GPS valido encontrado no arquivo');
  }

  // Fallback timestamps if CSV has no time column
  if (!startTs) startTs = new Date().toISOString();
  if (!endTs) endTs = new Date(new Date(startTs).getTime() + gps.length * 200).toISOString();

  const durationSeconds = Math.max(0, (new Date(endTs).getTime() - new Date(startTs).getTime()) / 1000);

  // Build optional RTK summary
  let rtk_summary = null;
  if (rtkSamples > 0 || hAccN > 0) {
    const total = rtkSamples || 1;
    rtk_summary = {
      samples: rtkSamples,
      fix_pct: Math.round(rtkCounts.Fix * 1000 / total) / 10,
      float_pct: Math.round(rtkCounts.Float * 1000 / total) / 10,
      single_pct: Math.round(rtkCounts.Single * 1000 / total) / 10,
      avg_h_acc_m: hAccN > 0 ? Number((hAccSum / hAccN).toFixed(4)) : null,
      avg_v_acc_m: vAccN > 0 ? Number((vAccSum / vAccN).toFixed(4)) : null,
      satellites_max: satsMax || null,
      has_rtk: rtkSamples > 0,
    };
  }

  return {
    drone_model: drone || 'DJI',
    drone_serial: serial || '',
    start_time: startTs,
    end_time: endTs,
    duration_seconds: durationSeconds,
    distance_meters: distMeters,
    max_altitude_meters: maxAlt,
    max_speed_ms: maxSpeed,
    battery_start_pct: firstBat,
    battery_end_pct: lastBat,
    home_lat: homeLat ?? firstLat,
    home_lng: homeLng ?? firstLng,
    gps_track: gps,
    file_name: name,
    detected_format: /csv$/i.test(name) ? 'CSV' : 'TXT',
    rtk_summary,
  };
}

export default parseDjiLog;
